import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import uniqueId from 'lodash/uniqueId';
import debounce from 'lodash/debounce';

import SearchBox from '@adsk/bim360-matrix-react-search-box';
import SearchBoxSuggestedResults from '@adsk/bim360-matrix-react-search-box-suggested-results';

import './_SearchBoxDropdown.scss';

/**
 * SearchBoxDropdown - SearchBox with suggest results drop down
 */
class SearchBoxDropdown extends React.Component {
  /**
   * constructor function
   */
  constructor(props) {
    super(props);

    this.state = {
      searchExpression: this.props.searchExpression,
      isFocused: false,
      /* currentSelectedRow works in the following way:
       * 0 : the SearchBox input (no dropdown selection)
       * 1-suggestionList.length : The suggested results rows
       *
       * Because of the way it works, when we want to refer to a suggested result, it will be (currentSelectedRow - 1)
       * The -1 is because of 0 which is not in the suggested results reducer
       */
      currentSelectedRow: 0
    };

    this.elementId = uniqueId('SearchBoxDropdown_'); // unique id for this element - used for click-outside handling

    this._handleSearchBoxOnChange = this._handleSearchBoxOnChange.bind(this);
    this._handleOnSearch = this._handleOnSearch.bind(this);
    this._handleOnClear = this._handleOnClear.bind(this);
    this._handleOnSuggestedResultRowSelect = this._handleOnSuggestedResultRowSelect.bind(this);
    this.setCurrentSelectedRow = this.setCurrentSelectedRow.bind(this);
    this._handleOnFocus = this._handleOnFocus.bind(this);
    this.searchForSuggestedResults = debounce(
      this.searchForSuggestedResults,
      this.props.searchForSuggestedResultsDebounce
    );
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    // Overriding searchExpression handling
    if (nextProps.searchExpression !== this.props.searchExpression) {
      this.setState({ searchExpression: nextProps.searchExpression });
    }
  }

  /**
   * componentDidUpdate
   */
  componentDidUpdate(prevProps, prevState) {
    // Searching for suggest results if search expression changed
    if (prevState.searchExpression !== this.state.searchExpression) {
      this.searchForSuggestedResults();
    }
  }

  /**
   * react-clickoutside uses this as a callback (so it is indeed in use and you can ignore the WebStorm marking)
   */
  handleClickOutside() {
    this.setState({ isFocused: false });
  }

  /**
   * Unfocus the component and resets the currentSelectedRow
   */
  closeDropdown() {
    this.setState({ isFocused: false, currentSelectedRow: 0 });
  }

  /**
   * Handles the changes received from the SearchBox-input component and adds them to state.
   * Also focuses the component to support refocusing after search has been triggered.
   * @param searchExpression - the new searchExpression string
   */
  _handleSearchBoxOnChange(searchExpression) {
    this.setState({ searchExpression, isFocused: true });
    this.props.onChange && this.props.onChange(searchExpression);
  }

  /**
   * Handles the search operation
   */
  _handleOnSearch() {
    this.closeDropdown();
    this.props.onSearch(this.state.searchExpression);
  }

  /**
   * Handles the clear operation
   */
  _handleOnClear() {
    this.setState({ searchExpression: '' }, () => {
      this.props.onClear ? this.props.onClear() : this._handleOnSearch();
    });
  }

  /**
   * Handles select event of a suggested result
   * @param entity - instance or template
   */
  _handleOnSuggestedResultRowSelect(entity) {
    this.props.onSuggestedResultRowSelect(entity, false);
    this.closeDropdown();
  }

  /**
   * Handles different special inputs
   * @param e - the event passed from input component
   */
  _handleKeyDown(e) {
    const ENTER = 13;
    const ESC = 27;
    const UP = 38;
    const DOWN = 40;

    const currentSelectedRow = this.state.currentSelectedRow; // Read comment in constructor for an explanation on this

    switch (e.keyCode) {
      case ENTER:
        e.preventDefault();
        currentSelectedRow === 0
          ? this._handleOnSearch(this.state.searchExpression)
          : this._handleOnSuggestedResultRowSelect(
            this.props.suggestionList[currentSelectedRow - 1]
          );
        return;
      case ESC:
        e.preventDefault();
        this._handleOnClear();
        return;
      case DOWN:
        e.preventDefault();
        currentSelectedRow < this.props.suggestionList.length
          ? this.setState({ currentSelectedRow: currentSelectedRow + 1 })
          : this.setState({ currentSelectedRow: 0 });
        return;
      case UP:
        e.preventDefault();
        currentSelectedRow > 0
          ? this.setState({ currentSelectedRow: currentSelectedRow - 1 })
          : this.setState({ currentSelectedRow: this.props.suggestionList.length });
        return;
      default:
        return;
    }
  }

  /**
   * Sets current selected row based on selected suggested result
   * @param index - the index of the suggested result in suggestionList
   */
  setCurrentSelectedRow(index) {
    this.setState({ currentSelectedRow: index + 1 });
  }

  /**
   * Searches for suggested results based on searchExpression
   */
  searchForSuggestedResults() {
    this.props.searchForSuggestedResults(this.state.searchExpression);

    // Resets the currentSelectedRow to prevent edge cases and undesired behavior
    this.setState({ currentSelectedRow: 0 });
  }

  _handleOnFocus() {
    this.setState({ isFocused: true });
    if (this.mainSearchDivElement) {
      this.mainSearchDivElement.blur();
    }
  }

  /**
   * render function
   */
  render() {
    const classes = classNames('SearchBoxDropdown', this.props.className);

    // The tabIndex makes the div focusable, giving it onKeyDown and focus handling.
    return (
      <div
        className={classes}
        id={this.elementId}
        tabIndex="0"
        ref={mainSearchDivElement => {
          this.mainSearchDivElement = mainSearchDivElement;
        }}
        onKeyDown={e => this._handleKeyDown(e)}
        onFocus={this._handleOnFocus}
      >
        <SearchBox
          searchExpression={this.state.searchExpression}
          inputProps={this.props.inputProps}
          placeholder={this.props.placeholder}
          onChange={this._handleSearchBoxOnChange}
          onSearch={this._handleOnSearch}
          onClear={this._handleOnClear}
          isFocused={this.state.isFocused}
          clearDisabled={this.props.clearDisabled}
          searchDisabled={this.props.searchDisabled}
          inputDisabled={this.props.inputDisabled}
        />
        <SearchBoxSuggestedResults
          suggestionList={this.props.suggestionList}
          isVisible={this.state.isFocused && this.state.searchExpression !== ''}
          onSuggestedResultRowSelect={this._handleOnSuggestedResultRowSelect}
          currentSelectedRow={this.state.currentSelectedRow}
          setCurrentSelectedRow={this.setCurrentSelectedRow}
          resetSelection={() => this.setCurrentSelectedRow(-1)}
          searchExpression={this.state.searchExpression}
          suggestionDataGetter={this.props.suggestionDataGetter}
          suggestionPrefixRenderer={this.props.suggestionPrefixRenderer}
        />
      </div>
    );
  }
}

SearchBoxDropdown.propTypes = {
  /**
   * A custom className for component container
   */
  className: PropTypes.string,
  /**
   * A placeholder text to be displayed when the search expression is empty
   */
  placeholder: PropTypes.string,
  /**
   * Optional controlled searchExpression prop
   */
  searchExpression: PropTypes.string,
  /**
   * A callback function for on change event
   * signature: (string) => void
   */
  onChange: PropTypes.func,
  /**
   * A callback function for on search event
   */
  onSearch: PropTypes.func.isRequired,
  /**
   * A callback function for on clear event
   */
  onClear: PropTypes.func,
  /**
   * A callback function for the event of selecting search result suggestion
   */
  onSuggestedResultRowSelect: PropTypes.func.isRequired,
  /**
   * List of search suggestions to appear in the dropdown
   */
  suggestionList: PropTypes.array.isRequired,
  /**
   * A callback function for the event of fetching search result suggestions
   */
  searchForSuggestedResults: PropTypes.func.isRequired,
  /**
   * A callback function for the event of clearing search result suggestions
   */
  clearSuggestedResults: PropTypes.func,
  /**
   * Time in milliseconds for a debounce between search fetching result suggestions
   */
  searchForSuggestedResultsDebounce: PropTypes.number,
  /**
   * Whether the input should be disabled
   */
  inputDisabled: PropTypes.bool,
  /**
   * Whether the search button should be disabled
   */
  searchDisabled: PropTypes.bool,
  /**
   * Whether the clear button should be disabled
   */
  clearDisabled: PropTypes.bool,
  /**
   * Additional input props
   */
  inputProps: PropTypes.object,
  /**
   * Data getter for suggestions that will be passed to SearchBoxSuggestedResults.
   * Please look at SearchBoxSuggestedResults for more details.
   */
  suggestionDataGetter: PropTypes.func,
  /**
   * Prefix renderer for suggestions that will be passed to SearchBoxSuggestedResults.
   * Please look at SearchBoxSuggestedResults for more details.
   */
  suggestionPrefixRenderer: PropTypes.func
};

SearchBoxDropdown.defaultProps = {
  searchForSuggestedResultsDebounce: 500,
  searchExpression: ''
};

export default SearchBoxDropdown;
